import React, { useEffect, useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'

import { getElectricityGridOperator } from '../../../api/senecApiUtils'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { setCustomerValuesGeneratorSlice } from '../../../store/slices/customerInputValuesSlice'
import { setElectricityGridOperatorSlice } from '../../../store/slices/electricityGridOperatorSlice'
import { customerValuesType } from '../../../types'
import { FormInput, FormDate, FormRadioItem, FormGroup, AddressWithValidation } from '../../molecules'

type GeneratorProps = {
  nextPage: string,
  prevPage:string,
  changeActivePage: (pageUrl: string) => void
}

const Component = ({ nextPage, prevPage, changeActivePage }: GeneratorProps) => {
  const dispatch = useAppDispatch()

  const customerInputValues = useAppSelector(state => state.customervalues)
  const defaultFormValues = { ...customerInputValues['p-generator'] }
  defaultFormValues['generator-anlage-inbetriebnahme'] = customerInputValues['p-intro']['generator-anlage-inbetriebnahme']
  defaultFormValues['generator-anlage-leistung'] = customerInputValues['p-intro']['pv-nominal-power-kwp']
  defaultFormValues['generator-weitere-anlagenbetreiber-name'] = customerInputValues['p-customer']['customer-nachname']
  defaultFormValues['generator-weitere-anlagenbetreiber-vorname'] = customerInputValues['p-customer']['customer-vorname']
  defaultFormValues['generator-email'] = customerInputValues['p-customer']['customer-emailAddresse']

  const { register, handleSubmit, control, watch, setValue, getValues, formState: { errors }, clearErrors } = useForm<customerValuesType.Generator>({
    mode: 'onChange',
    defaultValues: defaultFormValues
  })
  const watchDifferentPVAddress = watch('pv-address')

  const [showExtraOperator, setShowExtraOperator] = useState(customerInputValues['p-generator']['generator-extra-operator'])

  useEffect(() => {
    register('generator-extra-operator')
  }, [register])

  const handleBack = () => {
    const formData = getValues()
    dispatch(setCustomerValuesGeneratorSlice(formData))
    changeActivePage(prevPage)
  }

  const onSubmitFn: SubmitHandler<customerValuesType.Generator> = data => {
    dispatch(setCustomerValuesGeneratorSlice(data))

    let postalCode
    if (data['generator-anlagenstandort-postleitzahl']) {
      postalCode = data['generator-anlagenstandort-postleitzahl']
    } else {
      postalCode =
        customerInputValues['p-customer']['customer-postleitzahl']
    }
    getElectricityGridOperator(postalCode)
      .then(data => {
        if (data) {
          // Store data in State to use if in next step.
          dispatch(setElectricityGridOperatorSlice(JSON.parse(data)))
        } else {
          // In case we receive no data mocked data is shown.
          // eslint-disable-next-line no-console
          console.log(
            'No data was received from the Electricity Grid Operator endpoint.'
          )
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.log(`error: ${error}`)
      })

    changeActivePage(nextPage)
  }

  return (
    <div className="react-form__page react-form__page--generator">
      <div className="react-form__page__header relative bg-light pt-10">
        <div className="react-form__page__intro-text text-center pb-10">
          <h1 className="h2">Ihre PV-Anlage</h1>
          <p>
            Für die Einspeisung in Ihre SENEC.Cloud nutzen Sie die Überschüsse
            Ihrer PV-Anlage. Zur Ermittlung Ihrer Einspeisung in die
            SENEC.Cloud treten Sie die Einspeisevergütung, die Sie von Ihrem
            Netzbetreiber erhalten, an uns ab.
            <br />
            Hierfür benötigen wir die folgenden Daten zu Ihrer PV-Anlage, die
            Sie dem <b>Einspeisevertrag mit Ihrem Netzbetreiber</b> entnehmen
            können.
          </p>
        </div>
      </div>
      <div className="react-form__page--intro__body">
        <form>
          <div className="bg-gray-lighter p-6 md:p-10 xl:p-16">
            <h2 className="h3 mb-10">Erzeugungsanlage</h2>
            <div className="flex flex-wrap justify-between -mx-4">
              <FormDate
                disabled={true}
                errors={errors}
                register={register}
                control={control}
                id="generator-anlage-inbetriebnahme"
                label="Inbetriebnahmedatum"
                className="w-full sm:w-1/2 px-4"
                info="Hier haben wir das Inbetriebnahmedatum Ihrer PV-Anlage hinterlegt."
                peekNextMonth={true}
                showMonthDropdown={true}
                showYearDropdown={true}
                dropdownMode="select"
                validations={{
                  required: 'Bitte füllen Sie dieses Feld aus.'
                }}
              />
              <FormInput
                id="generator-anlage-leistung"
                label="Leistung der PV-Anlage"
                className="w-full sm:w-1/2 px-4"
                measurement="kWp"
                placeholder="Leistung der PV-Anlage"
                errors={errors}
                register={register}
                disabled={true}
              />
              <FormInput
                id="generator-contract-number"
                label="Vertragsnummer beim Netzbetreiber"
                className="w-full lg:w-1/2 px-4"
                info="Einige Netzbetreiber benötigen für die Umsetzung der Abtretung die Vertragsnummer Ihres
                      Einspeisevertrags. Wenn Sie noch keine Vertragsnummer bei Ihrem Netzbetreiber haben, können Sie
                      diese später nachreichen."

                placeholder="Vertragsnummer beim Netzbetreiber"
                errors={errors}
                register={register}
              />
            </div>
            <h2 className="h3 my-10">Anlagenbetreiber</h2>
            <div className="flex flex-wrap -mx-4">
              <div className="w-full mb-10 px-4">
              Bitte überprüfen Sie die Angaben zum Anlagenbetreiber in Ihrem Einspeisevertrag von Ihrem Netzbetreiber.
              Der Anlagenbetreiber muss mit diesen Angaben übereinstimmen,
              da sonst eine Umsetzung der Abtretung von Ihrem Verteilnetzbetreiber abgelehnt wird.
              </div>
              <FormInput
                disabled={true}
                id="generator-weitere-anlagenbetreiber-vorname"
                label="Vorname"
                className="w-full sm:w-4/12 px-4"
                placeholder="Vorname"
                errors={errors}
                register={register}
              />
              <FormInput
                disabled={true}
                id="generator-weitere-anlagenbetreiber-name"
                label="Name"
                className="w-full sm:w-4/12 px-4"
                placeholder="Name"
                errors={errors}
                register={register}
              />
              <FormInput
                disabled={true}
                id="generator-email"
                label="E-Mail Adresse"
                className="w-full sm:w-4/12 px-4"
                placeholder="E-Mail Adresse"
                errors={errors}
                register={register}
              />
            </div>
            {showExtraOperator && (
              <>
                <h4 className="mt-4 mb-10">Weiterer Anlagenbetreiber</h4>
                <div className="flex flex-wrap -mx-4">
                  <FormInput
                    id="generator-weitere-anlagenbetreiber-vorname2"
                    label="Vorname"
                    className="w-full sm:w-4/12 px-4"
                    placeholder="Vorname"
                    errors={errors}
                    register={register}
                    validations={{
                      required: 'Bitte füllen Sie dieses Feld aus.',
                      pattern: {
                        value: /^[a-zA-Z0-9äöüÄÖÜß]+[a-zA-Z0-9äöüÄÖÜß\-\s]*[a-zA-Z0-9äöüÄÖÜß]+$/,
                        message: 'Bitte geben Sie den Vornamen ohne Leerzeichen (auch am Anfang und am Ende) ein.'
                      }
                    }}
                  />
                  <FormInput
                    id="generator-weitere-anlagenbetreiber-name2"
                    label="Name"
                    className="w-full sm:w-4/12 px-4"
                    placeholder="Name"
                    errors={errors}
                    register={register}
                    validations={{
                      required: 'Bitte füllen Sie dieses Feld aus.',
                      pattern: {
                        value: /^[a-zA-Z0-9äöüÄÖÜß]+[a-zA-Z0-9äöüÄÖÜß\-\s]*[a-zA-Z0-9äöüÄÖÜß]+$/,
                        message: 'Bitte geben Sie den Nachnamen ohne Leerzeichen (auch am Anfang und am Ende) ein.'
                      }
                    }}
                  />
                  <FormInput
                    id="generator-email2"
                    label="E-Mail Adresse"
                    className="w-full sm:w-4/12 px-4"
                    placeholder="E-Mail Adresse"
                    errors={errors}
                    register={register}
                    validations={{
                      required: 'Bitte füllen Sie dieses Feld aus.',
                      pattern: {
                        value: /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                        message: 'Die E-Mail Adresse ist nicht gültig.'
                      }
                    }}
                  />
                </div>
              </>
            )}
            <div className="flex flex-wrap">
              <qz-button
                className="float-left"
                onClick={() => {
                  setValue(
                    'generator-extra-operator',
                    !showExtraOperator
                  )
                  setShowExtraOperator(!showExtraOperator)
                }}
              >
                {showExtraOperator ? 'Anlagenbetreiber entfernen' : 'Anlagenbetreiber hinzufügen'}
              </qz-button>
            </div>
            <h2 className="h3 my-10">Anlagenstandort</h2>
            <div className="flex flex-wrap">
              <div className="w-full mb-4">
                <FormGroup
                  id="customer-type-group"
                  errors={errors}
                  label="Befindet sich Ihre PV-Anlage an Ihrer Lieferanschrift?"
                >
                  <FormRadioItem
                    name="pv-address"
                    id="pv-address-yes"
                    value="1"
                    label="Ja"
                    className="inline-block"
                    register={register}
                    errors={errors}
                    defaultChecked={customerInputValues['p-generator']['pv-address'] === '1'}
                    validations={{
                      required: 'Bitte füllen Sie dieses Feld aus.'
                    }}
                  />
                  <FormRadioItem
                    name="pv-address"
                    id="pv-address-no"
                    value="0"
                    label="Nein"
                    className="inline-block md:ml-4"
                    register={register}
                    errors={errors}
                    defaultChecked={customerInputValues['p-generator']['pv-address'] === '0'}
                    validations={{
                      required: 'Bitte füllen Sie dieses Feld aus.'
                    }}
                  />
                </FormGroup>
              </div>
            </div>
            {watchDifferentPVAddress === '0' && (
              <div>
                <div className="w-full mb-10">
                  Bitte gib nachfolgend den Anlagenstandort an, sofern
                  dieser von der Lieferadresse abweicht.
                </div>
                <AddressWithValidation
                  sliceName="generatorAddressAbw"
                  prefix="generator-anlagenstandort"
                  errors={errors}
                  register={register}
                  watch={watch}
                  control={control}
                  clearErrors={clearErrors}
                  setValue={setValue}
                  cityOptionsInitial={typeof customerInputValues['p-generator']['generator-anlagenstandort-ort'] === 'object' ? [customerInputValues['p-generator']['generator-anlagenstandort-ort']] : []}
                  streetOptionsInitial={typeof customerInputValues['p-generator']['generator-anlagenstandort-strasse'] === 'object' ? [customerInputValues['p-generator']['generator-anlagenstandort-strasse']] : []}
                />
              </div>
            )}
          </div>
          <div className="clearfix bg-gray-lighter md:px-6 px-md-5 px-xl-7 pb-10">
            {prevPage && (
              <qz-button
                class="float-left"
                onClick={handleBack}
              >
                Zurück
              </qz-button>
            )}
            {nextPage && (
              <qz-button
                class="float-right"
                onClick={handleSubmit(onSubmitFn)}
                buttonType="submit"
              >
                Weiter
              </qz-button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default Component
